import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { SettingsService } from '../common/settings.service';
import { BlobFileSasUrl } from '../../models/blob-file-sas-url.model';
import { BlockPartnerReasonsByProgram } from '../../models/block-partner-reasons-by-program.model';
import { CommonInputModel } from '../../models/common-input.model';
import { ConciergeStatus } from '../../models/concierge-status.model';
import { EngagementActivityGeographyStatus } from '../../models/engagement-activity-geography-status.model';
import { EngagementActivityStatus } from '../../models/engagement-activity-status.model';
import { Engagement } from '../../models/engagement.model';
import { PartnerComplianceInfoEventAllocationData } from '../../models/partner-compliance-info-event-allocation-data.model';
import { PartnerComplianceInformation } from '../../models/partner-compliance-information.model';
import { PartnerConcierge } from '../../models/partner-concierge.model';
import { PartnerFundstatus } from '../../models/partner-fund-status.model';
import { PartnerInformation } from '../../models/partner-information.model';
import { PartnerOneAreas } from '../../models/partner-one-areas.model';
import { PartnerOneCountries } from '../../models/partner-one-countries.model';
import {
  PartnerPaymentDetails,
  FiscalYear,
  ProgramAgreementKeyDetail
} from '../../models/partner-payment-details.model';
import {
  PCInfoEventAllocationRequest,
  CurrentFiscalYearAndQuarter
} from '../../models/pc-info-event-allocation-request.model';
import { UpdateConciergeRequest } from '../../models/update-concierge-request.model';
import { UpdateCustomerAckDocument } from '../../models/update-customer-ack-document.model';
import { UpdateWorkshopDateInput } from '../../models/update-workshop-date-input.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private http: HttpClient,
    public settings: SettingsService
  ) {}

  getLoggedInUserName(): Observable<any> {
    return this.http.get(
      this.settings.baseUrl + 'api/users/GetLoggedInUserName'
    );
  }

  getAllEngagements(mpnid: string): Observable<Engagement[]> {
    return this.http.get<Engagement[]>(
      this.settings.baseUrl + 'api/Dashboard/GetAllEngagements/mpnid/' + mpnid
    );
  }

  getPartnerFundStatus(mpnid: string): Observable<PartnerFundstatus> {
    return this.http.get<PartnerFundstatus>(
      this.settings.baseUrl +
        'api/Dashboard/GetPartnerFundStatus/mpnid/' +
        mpnid
    );
  }

  GetEngagementActivityStatus(
    mpnid: string
  ): Observable<EngagementActivityStatus[]> {
    return this.http.get<EngagementActivityStatus[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetEngagementActivityStatus/mpnId/' +
        mpnid
    );
  }

  GetEngagementActivityStatus_V1(
    mpnid: string
  ): Observable<EngagementActivityStatus[]> {
    return this.http.get<EngagementActivityStatus[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetEngagementActivityStatus_V1/mpnId/' +
        mpnid
    );
  }

  GetEngagementActivityGeographyStatus(
    mpnid: string,
    AN: string
  ): Observable<EngagementActivityGeographyStatus[]> {
    return this.http.get<EngagementActivityGeographyStatus[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetEngagementCountByPartner/mpnId/' +
        mpnid +
        '/AN/' +
        AN
    );
  }

  GetEngagementActivityGeographyStatusEligible(
    mpnid: string,
    AN: string
  ): Observable<EngagementActivityGeographyStatus[]> {
    return this.http.get<EngagementActivityGeographyStatus[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetEngagementCountByPartnerEligible/mpnId/' +
        mpnid +
        '/AN/' +
        AN
    );
  }

  getPartnerInformation(mpnid: string): Observable<PartnerInformation> {
    return this.http.get<PartnerInformation>(
      this.settings.baseUrl +
        'api/Dashboard/GetPartnerInformation/mpnId/' +
        mpnid
    );
  }

  getPartnerAndPaymentDetails(
    input: CommonInputModel
  ): Observable<PartnerPaymentDetails> {
    return this.http.post<PartnerPaymentDetails>(
      this.settings.baseUrl + 'api/Dashboard/GetPartnerAndPaymentDetails',
      input
    );
  }

  getFiscalYear(): Observable<FiscalYear[]> {
    return this.http.get<FiscalYear[]>(
      this.settings.baseUrl + 'api/Dashboard/GetFiscalYear'
    );
  }
  GetProgramAgreement(
    mpnId: string,
    year: number
  ): Observable<ProgramAgreementKeyDetail> {
    return this.http.get<ProgramAgreementKeyDetail>(
      this.settings.baseUrl +
        'api/Dashboard/GetProgramAgreement/mpnId/' +
        mpnId +
        '/year/' +
        year
    );
  }

  previewFile(file: UpdateCustomerAckDocument): any {
    const headers = new HttpHeaders();
    return this.http.post(
      this.settings.baseUrl + 'api/EngagementDetails/DownloadFile',
      file,
      { headers, responseType: 'blob' }
    );
  }

  getFileSasUrl(documentModel: UpdateCustomerAckDocument): Observable<string> {
    return this.http
      .post<BlobFileSasUrl>(
        this.settings.baseUrl + 'api/EngagementDetails/DownloadFileSASUrl',
        documentModel
      )
      .pipe(map(s => s.blobUrl));
  }

  getPreviewFileContent(documentModel: UpdateCustomerAckDocument): any {
    return this.getFileSasUrl(documentModel).pipe(
      mergeMap(blolFileSasUrl =>
        this.http.get(blolFileSasUrl, { responseType: 'blob' })
      )
    );
  }

  updateWorkshopDate(engagementId: number, workshopScheduleDate: Date) {
    const updateWorkshopDateModel: UpdateWorkshopDateInput = {
      engagementId: engagementId,
      workshopScheduleDate: workshopScheduleDate.toLocaleDateString()
    };

    return this.http.post(
      this.settings.baseUrl + 'api/Dashboard/UpdateWorkshopDate',
      updateWorkshopDateModel
    );
  }

  getConciergeStatus(): Observable<ConciergeStatus[]> {
    return this.http.get<ConciergeStatus[]>(
      this.settings.baseUrl + 'api/Dashboard/GetConciergeStatus'
    );
  }

  getConciergeData(
    mpnid: number,
    programTypeId: number
  ): Observable<PartnerConcierge> {
    return this.http.get<PartnerConcierge>(
      this.settings.baseUrl +
        'api/Dashboard/GetConciergeData/mpnId/' +
        mpnid +
        '/programtypeId/' +
        programTypeId
    );
  }

  getAllConciergeData(mpnid: number): Observable<PartnerConcierge[]> {
    return this.http.get<PartnerConcierge[]>(
      this.settings.baseUrl + 'api/Dashboard/GetAllConciergeData/mpnId/' + mpnid
    );
  }

  saveConciergeData(updateConciergeRequest: UpdateConciergeRequest) {
    return this.http.post(
      this.settings.baseUrl + 'api/Dashboard/SavePartnerConciergeInformation',
      updateConciergeRequest
    );
  }

  saveAllConciergeData(updateConciergeRequest: UpdateConciergeRequest[]) {
    return this.http.post(
      this.settings.baseUrl +
        'api/Dashboard/SaveAllPartnerConciergeInformation',
      updateConciergeRequest
    );
  }

  getPartnerComplianceInformation(
    mpnId: number
  ): Observable<PartnerComplianceInformation[]> {
    return this.http.get<PartnerComplianceInformation[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetPartnerComplianceDetails/mpnId/' +
        mpnId
    );
  }

  addPartnerComplianceInformation(
    partnerComplianceDetail: PartnerComplianceInformation[]
  ) {
    return this.http.post(
      this.settings.baseUrl + 'api/Dashboard/SavePatnerComplianceInformation',
      partnerComplianceDetail
    );
  }

  getPartnerBlockReasons(): Observable<BlockPartnerReasonsByProgram[]> {
    return this.http.get<BlockPartnerReasonsByProgram[]>(
      this.settings.baseUrl + 'api/Dashboard/GetBlockPartnerReasons'
    );
  }

  getPartnerComplianceAllocationData(
    request: PCInfoEventAllocationRequest
  ): Observable<PartnerComplianceInfoEventAllocationData> {
    return this.http.post<PartnerComplianceInfoEventAllocationData>(
      this.settings.baseUrl +
        'api/Dashboard/GetPartnerComplianceEventAllocationData',
      request
    );
  }

  getPartnerOneAreas(partnerOneId: string): Observable<PartnerOneAreas[]> {
    return this.http.get<PartnerOneAreas[]>(
      this.settings.baseUrl + 'api/Dashboard/GetPartnerOneAreas/' + partnerOneId
    );
  }

  getPartnerOneCountries(
    partnerOneId: string
  ): Observable<PartnerOneCountries[]> {
    return this.http.get<PartnerOneCountries[]>(
      this.settings.baseUrl +
        'api/Dashboard/GetPartnerOneCountries/' +
        partnerOneId
    );
  }

  getCurrentFiscalYearAndQuarter(): Observable<CurrentFiscalYearAndQuarter> {
    return this.http.get<CurrentFiscalYearAndQuarter>(
      this.settings.baseUrl + 'api/Dashboard/GetCurrentFiscalYearAndQuarter'
    );
  }
}
